const API_BASE = import.meta.env.VITE_API_URL ?? "";

let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, { method: "POST", credentials: "include" });
    return res.ok;
  } catch { return false; }
}

// Ensures only one refresh is ever in flight; concurrent callers share the promise.
function refreshOnce(): Promise<boolean> {
  if (!refreshing) refreshing = tryRefresh().finally(() => { refreshing = null; });
  return refreshing;
}

// --- Proactive session refresh -------------------------------------------------
// The access token is short-lived. Rather than waiting for a request to 401
// (which is user-visible latency and drops the session if the single retry
// fails), we refresh in the background shortly before the token expires and
// whenever the user returns to a tab that may have gone stale.

// Access-token lifetime in ms. Kept slightly below the server TTL so we always
// refresh before expiry. Server TTL is 30m; refresh at ~25m.
const ACCESS_TOKEN_TTL_MS = 30 * 60 * 1000;
const REFRESH_LEAD_MS = 5 * 60 * 1000; // refresh 5 min before expiry
// If the tab was hidden/idle longer than this, refresh on return.
const STALE_TAB_MS = ACCESS_TOKEN_TTL_MS - REFRESH_LEAD_MS;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let lastActivityAt = Date.now();
let proactiveStarted = false;

function armTimer() {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(async () => {
    // Only refresh while the tab is visible; hidden tabs refresh on return.
    if (typeof document === "undefined" || document.visibilityState === "visible") {
      await refreshOnce();
      lastActivityAt = Date.now();
    }
    armTimer();
  }, ACCESS_TOKEN_TTL_MS - REFRESH_LEAD_MS);
}

async function onVisible() {
  if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
  // If we've been away long enough that the token may have expired, refresh now.
  if (Date.now() - lastActivityAt >= STALE_TAB_MS) {
    await refreshOnce();
    lastActivityAt = Date.now();
    armTimer();
  }
}

/**
 * Begin proactively refreshing the session in the background. Idempotent.
 * Call once after the user is authenticated (e.g. from AuthProvider).
 */
export function startProactiveRefresh() {
  if (proactiveStarted || typeof window === "undefined") return;
  proactiveStarted = true;
  lastActivityAt = Date.now();
  armTimer();
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", onVisible);
}

/** Stop background refresh (e.g. on logout). Idempotent. */
export function stopProactiveRefresh() {
  if (!proactiveStarted || typeof window === "undefined") return;
  proactiveStarted = false;
  if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
  document.removeEventListener("visibilitychange", onVisible);
  window.removeEventListener("focus", onVisible);
}

/** Map HTTP status codes to user-friendly error messages */
function friendlyErrorMessage(status: number, serverMessage?: string): string {
  // Use the server's message if it's already user-friendly (not a raw enum or technical string)
  if (serverMessage && !serverMessage.includes("_") && serverMessage.length < 200) {
    return serverMessage;
  }
  switch (status) {
    case 403: return "Only the goal owner can do this";
    case 404: return "Not found — it may have been removed";
    case 409: return "This conflicts with another action — try refreshing";
    case 429: return "Too many requests — try again in a moment";
    default:
      if (status >= 500) return "Something went wrong. Your data is safe — try again.";
      return serverMessage ?? `Request failed (${status})`;
  }
}

export interface ApiError extends Error {
  status?: number;
  fieldErrors?: Record<string, string[]>;
}

export async function apiFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const doFetch = () => fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });

  let res = await doFetch();

  if (res.status === 401 && !path.includes("/auth/login") && !path.includes("/auth/register") && !path.includes("/auth/refresh")) {
    // Fallback: refresh reactively if a request slips through with an expired token.
    const ok = await refreshOnce();
    if (ok) res = await doFetch();
  }

  // Any successful authenticated response means the session is alive right now.
  if (res.ok) lastActivityAt = Date.now();

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = friendlyErrorMessage(res.status, body.error);
    const err = new Error(message) as ApiError;
    err.status = res.status;
    err.fieldErrors = body.fieldErrors;
    throw err;
  }
  if (res.status === 204) return null as T;
  return res.json();
}
